import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchEmployeeDetailedAttendance } from "../redux/actions/attendanceActions";
import {
  fetchDistricts,
  fetchMunicipalities,
} from "../redux/actions/locationActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import theme from "../components/shared/theme";
import {
  paginationButtonStyle,
  dropdownStyle,
} from "../components/shared/themeStyles";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const EmployeeDetailedAttendance = () => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);

  const { 
    employeeDetailedAttendance = [], 
    loading, 
    error, 
    detailedPagination 
  } = useSelector((state) => state.attendance);

  const { districts = [], municipalities = [] } = useSelector(
    (state) => state.location || {}
  );
  const { designations = [] } = useSelector((state) => state.designation || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloading, setDownloading] = useState({ excel: false, pdf: false });

  const itemsPerPage = 10;

  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${year}-${month}`);
  }, []);

  const getMonthDateRange = () => {
    if (!selectedMonth) return { fromDate: '', toDate: '' };
    const [year, month] = selectedMonth.split('-');
    const yearNum = parseInt(year);
    const monthNum = parseInt(month) - 1;
    const firstDay = new Date(Date.UTC(yearNum, monthNum, 1));
    const lastDay = new Date(Date.UTC(yearNum, monthNum + 1, 0));
    return {
      fromDate: firstDay.toISOString().split('T')[0],
      toDate: lastDay.toISOString().split('T')[0]
    };
  };

  const { fromDate, toDate } = getMonthDateRange();

  useEffect(() => {
    if (selectedMonth) {
      dispatch(
        fetchEmployeeDetailedAttendance({
          page: currentPage,
          limit: itemsPerPage,
          district: selectedDistrict,
          municipality: selectedMunicipality,
          fromDate,
          toDate,
          search: searchTerm,
          designation: selectedDesignation,
        })
      );
    }
  }, [
    dispatch, currentPage, selectedDistrict, selectedMunicipality,
    selectedMonth, fromDate, toDate, searchTerm, selectedDesignation,
  ]);

  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());
  }, [dispatch]);

  const selectedDistrictObj = districts.find((d) => d.name === selectedDistrict);
  const selectedDistrictId = selectedDistrictObj?.id;
  const filteredMunicipalities = selectedDistrictId
    ? municipalities.filter((m) => m.district_id === selectedDistrictId)
    : municipalities;

  useEffect(() => { setSelectedMunicipality(""); }, [selectedDistrict]);

  const getDatesInMonth = () => {
    if (!selectedMonth) return [];
    const [year, month] = selectedMonth.split('-');
    const yearNum = parseInt(year);
    const monthNum = parseInt(month) - 1;
    const date = new Date(Date.UTC(yearNum, monthNum, 1));
    const dates = [];
    while (date.getUTCMonth() === monthNum) {
      dates.push(new Date(date));
      date.setUTCDate(date.getUTCDate() + 1);
    }
    return dates;
  };

  const allDates = getDatesInMonth();
  const totalDays = allDates.length;

  const getMonthDisplay = () => {
    if (!selectedMonth) return '';
    const [year, month] = selectedMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const formatDayNumber = (date) => date.getUTCDate();
  const getDayAbbr = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getUTCDay()];
  };

  const getStatusDisplay = (status) => {
    if (!status) return '—';
    const s = status.toLowerCase();
    const map = {
      present: 'P',
      absent: 'A',
      late: 'L',
      'early departure': 'ED',
      holiday: 'H',
      leave: 'Leave',
      pending: 'PN',
      'half day': '½P',
      field_visit: 'FV',
      wop: 'WO',
      wo: 'WO',
      saturday: 'Sat',
      sunday: 'Sun',
      'saturday present': 'SP',
      'sunday present': 'SuP',
      'holiday present': 'HP',
      upcoming: '—',
    };
    return map[s] || status;
  };

  const getStatusColor = (status) => {
    if (!status) return '#000000';
    const s = status.toLowerCase();
    switch (s) {
      case 'present':
      case 'saturday present':
      case 'sunday present':
      case 'holiday present':
        return '#4caf50';
      case 'absent':
        return '#f44336';
      case 'late':
        return '#ff9800';
      case 'early departure':
        return '#9c27b0';
      case 'holiday':
        return '#ff9800';
      case 'leave':
        return '#2196f3';
      case 'pending':
        return '#ff9800';
      case 'half day':
        return '#8bc34a';
      case 'field_visit':
        return '#9c27b0';
      case 'saturday':
      case 'sunday':
      case 'wop':
      case 'wo':
        return '#757575';
      case 'upcoming':
        return '#cccccc';
      default:
        return '#000000';
    }
  };

  const getDayAttendance = (emp, date) => {
    if (!emp || !date) return null;
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return emp.attendance_matrix?.[dateStr];
  };

  const formatTime = (timeStr) => timeStr || '';

  const hexToArgb = (hex) => 'FF' + hex.replace('#', '').toUpperCase();

  const borderStyle = { style: 'thin', color: { argb: 'FF999999' } };

  // ---------- Excel Export (PDF‑style) ----------
  const handleDownloadExcel = async () => {
    if (downloading.excel) return;
    setDownloading(prev => ({ ...prev, excel: true }));
    try {
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getEmployeeDetailedAttendance?${queryParams}`,
        { headers: { authorization: token } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const reportData = data.data || [];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Employee Attendance", {
        views: [{ state: 'frozen', ySplit: 2 }],
      });

      // Title row (merged across all columns)
      worksheet.mergeCells(1, 1, 1, allDates.length + 2);
      const titleCell = worksheet.getCell(1, 1);
      titleCell.value = `Employee Detailed Attendance Report - ${getMonthDisplay()}`;
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
      worksheet.getRow(1).height = 25;

      // Header rows (day numbers + abbreviations)
      const headerRow1 = ['Emp ID', 'Emp Name'];
      const headerRow2 = ['', ''];
      allDates.forEach(date => {
        headerRow1.push(formatDayNumber(date));
        headerRow2.push(getDayAbbr(date));
      });
      worksheet.addRow(headerRow1); // row 2
      worksheet.addRow(headerRow2); // row 3

      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
      for (let col = 1; col <= allDates.length + 2; col++) {
        const cell1 = worksheet.getCell(2, col);
        const cell2 = worksheet.getCell(3, col);
        cell1.fill = headerFill; cell1.font = { bold: true, size: 10 };
        cell1.alignment = { horizontal: 'center', vertical: 'middle' };
        cell1.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
        cell2.fill = headerFill; cell2.font = { bold: true, size: 9 };
        cell2.alignment = { horizontal: 'center', vertical: 'middle' };
        cell2.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
      }

      let currentRow = 4;

      reportData.forEach((emp, empIdx) => {
        // Employee info row (merged, gray background)
        worksheet.mergeCells(currentRow, 1, currentRow, allDates.length + 2);
        const infoCell = worksheet.getCell(currentRow, 1);
        infoCell.value = `Emp Id: ${emp.emp_id}    Emp Name: ${emp.emp_name}`;
        infoCell.font = { bold: true, size: 11 };
        infoCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } };
        infoCell.alignment = { horizontal: 'left', vertical: 'middle' };
        worksheet.getRow(currentRow).height = 20;
        currentRow++;

        // Status row
        const statusRow = worksheet.addRow(['Status', '', ...allDates.map(date => {
          const att = getDayAttendance(emp, date);
          return att ? getStatusDisplay(att.status) : '—';
        })]);
        for (let col = 3; col <= allDates.length + 2; col++) {
          const cell = statusRow.getCell(col);
          const dateIdx = col - 3;
          const att = getDayAttendance(emp, allDates[dateIdx]);
          if (att && att.status) {
            cell.font = { color: { argb: hexToArgb(getStatusColor(att.status)) }, bold: true };
          }
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
        }
        statusRow.getCell(1).font = { bold: true };
        statusRow.getCell(1).border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
        statusRow.getCell(2).border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
        worksheet.getRow(currentRow).height = 18;
        currentRow++;

        // In‑time row
        const intimeRow = worksheet.addRow(['Intime', '', ...allDates.map(date => {
          const att = getDayAttendance(emp, date);
          return att?.check_in_time ? formatTime(att.check_in_time) : '';
        })]);
        for (let col = 1; col <= allDates.length + 2; col++) {
          intimeRow.getCell(col).border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
          intimeRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
        }
        intimeRow.getCell(1).font = { bold: true };
        currentRow++;

        // Out‑time row
        const outtimeRow = worksheet.addRow(['OutTime', '', ...allDates.map(date => {
          const att = getDayAttendance(emp, date);
          return att?.check_out_time ? formatTime(att.check_out_time) : '';
        })]);
        for (let col = 1; col <= allDates.length + 2; col++) {
          outtimeRow.getCell(col).border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
          outtimeRow.getCell(col).alignment = { horizontal: 'center', vertical: 'middle' };
        }
        outtimeRow.getCell(1).font = { bold: true };
        currentRow++;

        // Total hours row
        const totalRow = worksheet.addRow(['Total', '', ...allDates.map(date => {
          const att = getDayAttendance(emp, date);
          return att?.total_hours ? att.total_hours : '';
        })]);
        for (let col = 1; col <= allDates.length + 2; col++) {
          const cell = totalRow.getCell(col);
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
          cell.border = { top: borderStyle, bottom: borderStyle, left: borderStyle, right: borderStyle };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          if (col === 1) cell.font = { bold: true };
        }
        currentRow++;

        // Blank separator row
        worksheet.addRow([]);
        currentRow++;
      });

      // Column widths
      worksheet.columns.forEach((col, index) => {
        if (index === 0) col.width = 12;
        else if (index === 1) col.width = 25;
        else col.width = 8;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      const filename = `Employee_Attendance_${getMonthDisplay().replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Failed to download Excel:", err);
      alert(`Failed to download Excel: ${err.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, excel: false }));
    }
  };

  // ---------- PDF Export ----------
  const handleDownloadPDF = async () => {
    if (downloading.pdf) return;
    setDownloading(prev => ({ ...prev, pdf: true }));
    try {
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 10000,
        district: selectedDistrict,
        municipality: selectedMunicipality,
        fromDate,
        toDate,
        search: searchTerm,
        designation: selectedDesignation,
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getEmployeeDetailedAttendance?${queryParams}`,
        { headers: { authorization: token } }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const reportData = data.data || [];

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'bold');
      doc.text(`Employee Detailed Attendance Report - ${getMonthDisplay()}`, 14, 10);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      let filterText = '';
      if (selectedDistrict) filterText += `District: ${selectedDistrict}`;
      if (selectedMunicipality) filterText += ` | Municipality: ${selectedMunicipality}`;
      if (selectedDesignation) filterText += ` | Designation: ${selectedDesignation}`;
      if (searchTerm) filterText += ` | Search: ${searchTerm}`;
      doc.text(filterText || 'All Records', 14, 16);

      let currentPage = 1;
      let yOffset = 25;

      for (const emp of reportData) {
        const dates = Object.keys(emp.attendance_matrix || {}).sort();
        if (dates.length > 0) {
          if (yOffset > 180) {
            doc.addPage();
            currentPage++;
            yOffset = 20;
            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text(`Employee Detailed Attendance Report - ${getMonthDisplay()}`, 14, 10);
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.text(filterText || 'All Records', 14, 16);
          }

          doc.setFillColor(240, 240, 240);
          doc.rect(14, yOffset - 4, 260, 8, 'F');
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'bold');
          doc.text(`Emp Id: ${emp.emp_id}`, 16, yOffset);
          doc.text(`Emp Name: ${emp.emp_name}`, 80, yOffset);
          yOffset += 6;

          const headers = [['', ...dates.map(date => {
            const d = new Date(date + 'T12:00:00Z');
            return `${d.getUTCDate()}\n(${getDayAbbr(d)})`;
          })]];

          const tableData = [];
          tableData.push(['Status', ...dates.map(date => {
            const det = emp.attendance_matrix[date];
            return { content: getStatusDisplay(det?.status), styles: { textColor: getStatusColor(det?.status), fontStyle: 'bold' } };
          })]);
          tableData.push(['Intime', ...dates.map(date => emp.attendance_matrix[date]?.check_in_time || '')]);
          tableData.push(['OutTime', ...dates.map(date => emp.attendance_matrix[date]?.check_out_time || '')]);
          tableData.push(['Total', ...dates.map(date => emp.attendance_matrix[date]?.total_hours ? `${emp.attendance_matrix[date].total_hours}` : '')]);

          autoTable(doc, {
            startY: yOffset,
            head: headers,
            body: tableData,
            theme: 'grid',
            styles: { fontSize: 7, cellPadding: 1.5, lineColor: [0,0,0], lineWidth: 0.1, halign: 'center', valign: 'middle' },
            headStyles: { fillColor: [220,220,220], textColor: [0,0,0], fontStyle: 'bold', halign: 'center', fontSize: 6 },
            columnStyles: { 0: { cellWidth: 20, fontStyle: 'bold', fillColor: [240,240,240], halign: 'left', fontSize: 7 } },
            margin: { left: 14, right: 14 },
            didDrawPage: (data) => {
              doc.setFontSize(8);
              doc.setTextColor(100,100,100);
              doc.text(`Page ${currentPage}`, 280, 200, { align: 'right' });
            }
          });

          yOffset = doc.lastAutoTable.finalY + 8;
        }
      }

      const filename = `Employee_Attendance_${getMonthDisplay().replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error("Failed to download PDF:", err);
      alert(`Failed to download PDF: ${err.message}`);
    } finally {
      setDownloading(prev => ({ ...prev, pdf: false }));
    }
  };

  const scrollToLatest = () => {
    if (tableContainerRef.current) tableContainerRef.current.scrollLeft = tableContainerRef.current.scrollWidth;
  };
  const scrollToEarliest = () => {
    if (tableContainerRef.current) tableContainerRef.current.scrollLeft = 0;
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const value = `${year}-${month}`;
      const label = date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  const monthOptions = getMonthOptions();

  return (
    <div style={{ padding: theme.spacing.lg, backgroundColor: theme.colors.background, minHeight: "100vh" }}>
      <h1 style={{ fontSize: theme.font.size["2xl"], fontWeight: theme.font.weight.bold, color: theme.colors.grayDark, marginBottom: theme.spacing.lg }}>
        Employee Detailed Attendance
        {selectedMonth && (
          <span style={{ fontSize: theme.font.size.md, color: '#666', marginLeft: theme.spacing.md }}>
            {getMonthDisplay()} ({totalDays} days)
          </span>
        )}
      </h1>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: theme.spacing.sm, marginBottom: theme.spacing.lg, backgroundColor: "#fff", padding: theme.spacing.md, borderRadius: theme.radius.xl, boxShadow: theme.shadow.soft }}>
        <select value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }} style={dropdownStyle} disabled={downloading.excel || downloading.pdf}>
          {monthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} style={dropdownStyle} disabled={downloading.excel || downloading.pdf}>
          <option value="">All Districts</option>
          {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>
        <select value={selectedMunicipality} onChange={(e) => setSelectedMunicipality(e.target.value)} style={dropdownStyle} disabled={downloading.excel || downloading.pdf}>
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
        </select>
        <select value={selectedDesignation} onChange={(e) => setSelectedDesignation(e.target.value)} style={dropdownStyle} disabled={downloading.excel || downloading.pdf}>
          <option value="">All Designations</option>
          {designations.map((d, idx) => <option key={idx} value={d}>{d}</option>)}
        </select>
        <input type="text" placeholder="Search name, phone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ ...dropdownStyle, minWidth: "180px" }} disabled={downloading.excel || downloading.pdf} />
        <button onClick={() => {
          const now = new Date(); const year = now.getFullYear(); const month = String(now.getMonth() + 1).padStart(2, '0');
          setSelectedMonth(`${year}-${month}`); setSelectedDistrict(""); setSelectedMunicipality(""); setSelectedDesignation(""); setSearchTerm(""); setCurrentPage(1);
        }} disabled={downloading.excel || downloading.pdf} style={{ backgroundColor: (downloading.excel || downloading.pdf) ? theme.colors.gray : theme.colors.primary, color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: (downloading.excel || downloading.pdf) ? "not-allowed" : "pointer", fontWeight: "bold", minWidth: "80px" }}>
          Reset
        </button>
        <button onClick={handleDownloadExcel} disabled={downloading.excel || downloading.pdf} style={{ backgroundColor: downloading.excel ? theme.colors.gray : '#28a745', color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: (downloading.excel || downloading.pdf) ? "not-allowed" : "pointer", fontWeight: "bold", minWidth: "100px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center", gap: theme.spacing.sm, opacity: (downloading.excel || downloading.pdf) ? 0.7 : 1, transition: "all 0.3s ease" }}>
          {downloading.excel ? <><div className="spinner-small" /><span>Excel...</span></> : <><span>📊</span><span>Excel</span></>}
        </button>
        <button onClick={handleDownloadPDF} disabled={downloading.excel || downloading.pdf} style={{ backgroundColor: downloading.pdf ? theme.colors.gray : '#dc3545', color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: (downloading.excel || downloading.pdf) ? "not-allowed" : "pointer", fontWeight: "bold", minWidth: "100px", height: "38px", display: "flex", alignItems: "center", justifyContent: "center", gap: theme.spacing.sm, opacity: (downloading.excel || downloading.pdf) ? 0.7 : 1, transition: "all 0.3s ease" }}>
          {downloading.pdf ? <><div className="spinner-small" /><span>PDF...</span></> : <><span>📄</span><span>PDF</span></>}
        </button>
        <button onClick={scrollToEarliest} style={{ backgroundColor: '#6c757d', color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: "pointer", fontWeight: "bold", fontSize: "12px" }}>⬅ Earliest</button>
        <button onClick={scrollToLatest} style={{ backgroundColor: '#6c757d', color: "#fff", padding: `${theme.spacing.sm} ${theme.spacing.md}`, borderRadius: theme.radius.md, border: "none", cursor: "pointer", fontWeight: "bold", fontSize: "12px" }}>Latest ➡</button>
      </div>

      {/* Table Container */}
      <div ref={tableContainerRef} style={{ backgroundColor: "#fff", borderRadius: theme.radius.xl, boxShadow: theme.shadow.soft, overflowX: "auto", overflowY: "auto", position: "relative", maxHeight: "calc(100vh - 250px)", scrollBehavior: "smooth" }}>
        {loading && <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: theme.radius.xl }}><div className="spinner" /></div>}
        {(downloading.excel || downloading.pdf) && (
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(255,255,255,0.9)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 20, borderRadius: theme.radius.xl }}>
            <div className="spinner" />
            <p style={{ marginTop: theme.spacing.md, fontSize: theme.font.size.md, fontWeight: "bold", color: theme.colors.grayDark }}>
              Generating {downloading.excel ? 'Excel' : 'PDF'}... Please wait
            </p>
          </div>
        )}
        {error ? <div style={{ textAlign: "center", padding: "40px", color: "red" }}>{error}</div> : employeeDetailedAttendance.length === 0 ? <div style={{ textAlign: "center", padding: "40px" }}>No records found for {getMonthDisplay()}</div> : (
          <div style={{ minWidth: `${Math.max(1000, totalDays * 40 + 280)}px` }}>
            {/* Days Header */}
            <div style={{ display: "flex", backgroundColor: "#e0e0e0", borderBottom: "2px solid #333", fontWeight: "bold", position: "sticky", top: 0, zIndex: 2 }}>
              <div style={{ width: "80px", padding: "8px", borderRight: "1px solid #999", backgroundColor: "#d4d4d4", flexShrink: 0 }}>Emp ID</div>
              <div style={{ width: "160px", padding: "8px", borderRight: "1px solid #999", backgroundColor: "#d4d4d4", flexShrink: 0 }}>Emp Name</div>
              <div style={{ display: "flex" }}>
                {allDates.map((date, index) => (
                  <div key={index} style={{ width: "40px", padding: "4px", textAlign: "center", borderRight: index < allDates.length - 1 ? "1px solid #999" : "none", fontSize: "10px", backgroundColor: "#d4d4d4", flexShrink: 0 }}>
                    <div>{formatDayNumber(date)}</div>
                    <div style={{ fontSize: "8px", color: "#555" }}>({getDayAbbr(date)})</div>
                  </div>
                ))}
              </div>
            </div>

            {employeeDetailedAttendance.map((emp, empIdx) => (
              <div key={emp.emp_id}>
                {/* Status Row */}
                <div style={{ display: "flex", borderBottom: "1px solid #ccc", backgroundColor: empIdx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                  <div style={{ width: "80px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "11px", fontWeight: "bold", backgroundColor: "#e8e8e8", flexShrink: 0 }}>{emp.emp_id}</div>
                  <div style={{ width: "160px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "11px", fontWeight: "bold", flexShrink: 0 }}>{emp.emp_name}</div>
                  <div style={{ display: "flex" }}>
                    {allDates.map((date, dateIdx) => {
                      const attendance = getDayAttendance(emp, date);
                      return (
                        <div key={dateIdx} style={{ width: "40px", padding: "4px", textAlign: "center", borderRight: dateIdx < allDates.length - 1 ? "1px solid #ccc" : "none", fontSize: "10px", fontWeight: "bold", color: attendance ? getStatusColor(attendance.status) : '#000', flexShrink: 0 }}>
                          {attendance ? getStatusDisplay(attendance.status) : '—'}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* In Time Row */}
                <div style={{ display: "flex", borderBottom: "1px solid #ccc", backgroundColor: empIdx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                  <div style={{ width: "80px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "10px", color: "#666", backgroundColor: "#f0f0f0", flexShrink: 0 }}>Intime</div>
                  <div style={{ width: "160px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "10px", color: "#666", flexShrink: 0 }}>&nbsp;</div>
                  <div style={{ display: "flex" }}>
                    {allDates.map((date, dateIdx) => {
                      const attendance = getDayAttendance(emp, date);
                      return (
                        <div key={dateIdx} style={{ width: "40px", padding: "4px", textAlign: "center", borderRight: dateIdx < allDates.length - 1 ? "1px solid #ccc" : "none", fontSize: "9px", color: "#0066cc", flexShrink: 0 }}>
                          {attendance?.check_in_time ? formatTime(attendance.check_in_time) : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Out Time Row */}
                <div style={{ display: "flex", borderBottom: "1px solid #ccc", backgroundColor: empIdx % 2 === 0 ? "#fff" : "#f5f5f5" }}>
                  <div style={{ width: "80px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "10px", color: "#666", backgroundColor: "#f0f0f0", flexShrink: 0 }}>OutTime</div>
                  <div style={{ width: "160px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "10px", color: "#666", flexShrink: 0 }}>&nbsp;</div>
                  <div style={{ display: "flex" }}>
                    {allDates.map((date, dateIdx) => {
                      const attendance = getDayAttendance(emp, date);
                      return (
                        <div key={dateIdx} style={{ width: "40px", padding: "4px", textAlign: "center", borderRight: dateIdx < allDates.length - 1 ? "1px solid #ccc" : "none", fontSize: "9px", color: "#cc6600", flexShrink: 0 }}>
                          {attendance?.check_out_time ? formatTime(attendance.check_out_time) : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Total Hours Row */}
                <div style={{ display: "flex", borderBottom: empIdx < employeeDetailedAttendance.length - 1 ? "2px solid #333" : "none", backgroundColor: empIdx % 2 === 0 ? "#fff" : "#f5f5f5", marginBottom: empIdx < employeeDetailedAttendance.length - 1 ? "8px" : "0" }}>
                  <div style={{ width: "80px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "10px", color: "#666", backgroundColor: "#f0f0f0", flexShrink: 0 }}>Total</div>
                  <div style={{ width: "160px", padding: "4px 8px", borderRight: "1px solid #ccc", fontSize: "10px", color: "#666", flexShrink: 0 }}>&nbsp;</div>
                  <div style={{ display: "flex" }}>
                    {allDates.map((date, dateIdx) => {
                      const attendance = getDayAttendance(emp, date);
                      return (
                        <div key={dateIdx} style={{ width: "40px", padding: "4px", textAlign: "center", borderRight: dateIdx < allDates.length - 1 ? "1px solid #ccc" : "none", fontSize: "9px", fontWeight: "bold", color: attendance?.total_hours ? '#28a745' : '#999', flexShrink: 0 }}>
                          {attendance?.total_hours ? attendance.total_hours.replace(' Hr', '') : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ padding: theme.spacing.sm, backgroundColor: "#f8f9fa", borderTop: "1px solid #dee2e6", borderBottom: "1px solid #dee2e6", fontSize: "11px", display: "flex", flexWrap: "wrap", gap: "16px", marginTop: theme.spacing.md }}>
        <div><span style={{ color: '#4caf50', fontWeight: "bold" }}>P:</span> Present</div>
        <div><span style={{ color: '#f44336', fontWeight: "bold" }}>A:</span> Absent</div>
        <div><span style={{ color: '#ff9800', fontWeight: "bold" }}>L:</span> Late</div>
        <div><span style={{ color: '#9c27b0', fontWeight: "bold" }}>ED:</span> Early Departure</div>
        <div><span style={{ color: '#2196f3', fontWeight: "bold" }}>Leave:</span> Leave</div>
        <div><span style={{ color: '#ff9800', fontWeight: "bold" }}>H:</span> Holiday</div>
        <div><span style={{ color: '#ff9800', fontWeight: "bold" }}>PN:</span> Pending</div>
        <div><span style={{ color: '#8bc34a', fontWeight: "bold" }}>½P:</span> Half Day</div>
        <div><span style={{ color: '#9c27b0', fontWeight: "bold" }}>FV:</span> Field Visit</div>
        <div><span style={{ color: '#757575', fontWeight: "bold" }}>WO:</span> Week Off / WOP</div>
        <div><span style={{ color: '#4caf50', fontWeight: "bold" }}>HP/SP/SuP:</span> Holiday/Sat/Sun Present</div>
        <div><span style={{ color: '#757575', fontWeight: "bold" }}>Sat/Sun:</span> Weekend (no attendance)</div>
        <div><span style={{ color: '#757575', fontWeight: "bold" }}>—:</span> Upcoming / No record</div>
        <div><span style={{ color: '#6c757d', fontWeight: "bold" }}>↔️:</span> Scroll horizontally</div>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: theme.spacing.md, backgroundColor: "#f8f9fa", borderTop: "1px solid #dee2e6", marginTop: theme.spacing.sm }}>
        <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1 || loading || downloading.excel || downloading.pdf} style={{ ...paginationButtonStyle, backgroundColor: (currentPage === 1 || loading || downloading.excel || downloading.pdf) ? '#6c757d' : '#007bff', opacity: (currentPage === 1 || loading || downloading.excel || downloading.pdf) ? 0.5 : 1, cursor: (currentPage === 1 || loading || downloading.excel || downloading.pdf) ? "not-allowed" : "pointer" }}>Previous</button>
        <div style={{ display: "flex", alignItems: "center", gap: theme.spacing.sm }}>
          <span style={{ fontWeight: "bold" }}>Page {detailedPagination?.currentPage || 1} of {detailedPagination?.totalPages || 1}</span>
          <span style={{ color: "#6c757d" }}>•</span>
          <span>Total Records: {detailedPagination?.totalRecords || 0}</span>
          <span style={{ color: "#6c757d" }}>•</span>
          <span>Days: {totalDays}</span>
        </div>
        <button onClick={() => setCurrentPage((p) => Math.min(detailedPagination?.totalPages || 1, p + 1))} disabled={currentPage === detailedPagination?.totalPages || loading || downloading.excel || downloading.pdf} style={{ ...paginationButtonStyle, backgroundColor: (currentPage === detailedPagination?.totalPages || loading || downloading.excel || downloading.pdf) ? '#6c757d' : '#007bff', opacity: (currentPage === detailedPagination?.totalPages || loading || downloading.excel || downloading.pdf) ? 0.5 : 1, cursor: (currentPage === detailedPagination?.totalPages || loading || downloading.excel || downloading.pdf) ? "not-allowed" : "pointer" }}>Next</button>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          width: 40px; height: 40px;
          border: 3px solid #e0e0e0;
          border-top-color: #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        .spinner-small {
          width: 16px; height: 16px;
          border: 2px solid #ffffff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default EmployeeDetailedAttendance;